import { supabase } from '../supabase';
import { Property, ChatMessage } from '../types';

export const propertyService = {
  async saveProperty(property: Partial<Property>) {
    try {
      const { data, error } = await supabase
        .from('properties')
        .insert(property)
        .select()
        .single();
        
      if (error) {
        console.error('Error saving property:', error);
        throw this.handleDatabaseError(error);
      }
      
      return data;
    } catch (error) {
      console.error('Exception in saveProperty:', error);
      throw this.handleDatabaseError(error);
    }
  },
  
  async getProperties(userId: string) {
    try {
      // Verify session is valid before proceeding
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Authentication session required');
      }
      
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
        
      if (error) {
        console.error('Error getting properties:', error);
        throw this.handleDatabaseError(error);
      }
      
      return data || [];
    } catch (error) {
      console.error('Exception in getProperties:', error);
      throw this.handleDatabaseError(error);
    }
  },
  
  async deleteProperty(propertyId: string, userId: string) {
    try {
      // First verify session is valid
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Authentication session required');
      }
      
      // First delete chat messages related to this property
      const { error: chatMessagesError } = await supabase
        .from('chat_messages')
        .delete()
        .eq('property_id', propertyId);
        
      if (chatMessagesError) {
        console.error('Error deleting chat messages:', chatMessagesError);
      }
      
      // Then delete property images if they exist
      const { error: imagesError } = await supabase
        .from('property_images')
        .delete()
        .eq('property_id', propertyId);
        
      if (imagesError) {
        console.error('Error deleting property images:', imagesError);
      }
      
      // Finally delete the property itself
      const { error } = await supabase
        .from('properties')
        .delete()
        .match({ 
          id: propertyId,
          user_id: userId 
        });
        
      if (error) {
        console.error('Error deleting property:', error);
        throw this.handleDatabaseError(error);
      }
    } catch (error) {
      console.error('Exception in deleteProperty:', error);
      throw this.handleDatabaseError(error);
    }
  },
  
  async getPropertyWithMessages(propertyId: string) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Authentication session required');
      }
      
      const { data: property, error: propertyError } = await supabase
        .from('properties')
        .select('*')
        .eq('id', propertyId)
        .single();
        
      if (propertyError) {
        console.error('Error getting property:', propertyError);
        throw this.handleDatabaseError(propertyError);
      }
      
      const { data: messages, error: messagesError } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('property_id', propertyId)
        .order('created_at', { ascending: true });
        
      if (messagesError) {
        console.error('Error getting chat messages:', messagesError);
        throw this.handleDatabaseError(messagesError);
      }
      
      return {
        ...property,
        chat_messages: messages || []
      };
    } catch (error) {
      console.error('Exception in getPropertyWithMessages:', error);
      throw this.handleDatabaseError(error);
    }
  },
  
  // Helper method to handle database errors
  handleDatabaseError(error: any): Error {
    // Check for role-related errors
    if (error.code === '22023' && error.message?.includes('role')) {
      console.error('Database role error detected:', error);
      
      // Try to refresh the session
      supabase.auth.refreshSession().catch(e => {
        console.error('Failed to refresh session:', e);
      });
      
      return new Error('Database permission error. Your session will be refreshed. Please try again in a moment.');
    }
    
    // Check for auth-related errors
    if (error.code === 'PGRST301' || error.message?.includes('JWT')) {
      return new Error('Authentication error. Please sign in again.');
    }
    
    // For other errors
    return error instanceof Error ? error : new Error(error.message || 'An unknown error occurred');
  }
};
